import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { decryptOnlyItemAtPriceQueryValue, decodeOnlyItemAtPriceFromUrl } from "./oipEncryption";
export var ONLY_ITEM_AT_PRICE_PARAM = "OnlyItemAtPrice";
export var ONLY_ITEM_AT_PRICE_STORAGE_PREFIX = "miscusi_onlyItemAtPrice:";
function getOipEncryptionKey() {
  return process.env.OIP_ENCRYPTION_KEY || "";
}
function isPlaintextOnlyItemAtPriceAllowed() {
  return process.env.OIP_ALLOW_PLAINTEXT === "true";
}

/**
 * Parse ?OnlyItemAtPrice=002146-18_003084-18 into [{ sku, price }, ...].
 * @param {string|null|undefined} value
 * @returns {{ sku: string, price: number }[]|null}
 */
export function parseOnlyItemAtPriceParam(value) {
  if (!value || typeof value !== "string") {
    return null;
  }
  var items = value.split("_").filter(Boolean).map(pair => {
    var lastDash = pair.lastIndexOf("-");
    if (lastDash <= 0) {
      return null;
    }
    var sku = pair.slice(0, lastDash);
    var price = parseFloat(pair.slice(lastDash + 1));
    if (!sku || Number.isNaN(price)) {
      return null;
    }
    return {
      sku,
      price
    };
  }).filter(Boolean);
  return items.length ? items : null;
}

/**
 * Accepts encrypted (oip1.*) or, when OIP_ALLOW_PLAINTEXT=true, legacy plaintext values.
 * @param {string|null|undefined} value
 * @returns {{ sku: string, price: number }[]|null}
 */
export function resolveOnlyItemAtPriceParam(value) {
  if (!value || typeof value !== "string") {
    return null;
  }
  var normalizedValue = value.replace(/ /g, "+");
  if (decodeOnlyItemAtPriceFromUrl(normalizedValue)) {
    var key = getOipEncryptionKey();
    if (!key) {
      return null;
    }
    var decryptedItems = decryptOnlyItemAtPriceQueryValue(normalizedValue, key);
    return parseOnlyItemAtPriceParam(decryptedItems);
  }
  if (isPlaintextOnlyItemAtPriceAllowed()) {
    return parseOnlyItemAtPriceParam(normalizedValue);
  }
  return null;
}

/** @param {string|null|undefined} search */
export function parseOnlyItemAtPriceFromSearch(search) {
  var params = new URLSearchParams(search || "");
  return resolveOnlyItemAtPriceParam(params.get(ONLY_ITEM_AT_PRICE_PARAM));
}

/** @param {string|null|undefined} cps */
export function getOnlyItemAtPriceStorageKey(cps) {
  if (!cps) {
    return null;
  }
  return "".concat(ONLY_ITEM_AT_PRICE_STORAGE_PREFIX).concat(cps);
}

/** @param {string|null|undefined} cps */
export function readOnlyItemAtPriceParamFromSession(cps) {
  var key = getOnlyItemAtPriceStorageKey(cps);
  if (!key || typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage.getItem(key);
  } catch (_unused) {
    return null;
  }
}

/** @param {string|null|undefined} cps @param {string} rawValue */
export function writeOnlyItemAtPriceParamToSession(cps, rawValue) {
  var key = getOnlyItemAtPriceStorageKey(cps);
  if (!key || !rawValue || typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(key, rawValue);
  } catch (_unused2) {
    // ignore quota / private mode
  }
}

/** @param {string|null|undefined} cps */
export function clearOnlyItemAtPriceParamFromSession(cps) {
  var key = getOnlyItemAtPriceStorageKey(cps);
  if (!key || typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.removeItem(key);
  } catch (_unused3) {
    // ignore quota / private mode
  }
}

/**
 * @param {string|null|undefined} search
 * @returns {string|null|undefined} undefined when param absent, null when explicitly cleared
 */
export function getOnlyItemAtPriceParamFromSearch(search) {
  var params = new URLSearchParams(search || "");
  if (!params.has(ONLY_ITEM_AT_PRICE_PARAM)) {
    return undefined;
  }
  return params.get(ONLY_ITEM_AT_PRICE_PARAM);
}

/**
 * Resolve the whitelist from URL first, then scoped sessionStorage fallback.
 * Persists valid URL values per store (`cps`) for the current browser tab.
 *
 * @param {string|null|undefined} search
 * @param {string|null|undefined} cps
 * @returns {{ sku: string, price: number }[]|null}
 */
export function resolveOnlyItemAtPriceFromContext(search, cps) {
  var params = new URLSearchParams(search || "");
  var scopedCps = cps || params.get("cps") || null;
  var paramFromSearch = getOnlyItemAtPriceParamFromSearch(search);
  if (paramFromSearch === "") {
    if (scopedCps) {
      clearOnlyItemAtPriceParamFromSession(scopedCps);
    }
    return null;
  }
  if (paramFromSearch) {
    var normalizedParam = paramFromSearch.replace(/ /g, "+");
    var whitelist = resolveOnlyItemAtPriceParam(normalizedParam);
    if (whitelist && scopedCps) {
      writeOnlyItemAtPriceParamToSession(scopedCps, normalizedParam);
    }
    return whitelist;
  }
  if (!scopedCps) {
    return null;
  }
  var storedParam = readOnlyItemAtPriceParamFromSession(scopedCps);
  if (!storedParam) {
    return null;
  }
  return resolveOnlyItemAtPriceParam(storedParam);
}

/**
 * @param {{ sku: string, price: number }[]|null|undefined} whitelist
 * @param {string} sku
 * @returns {number|null}
 */
export function getOnlyItemAtPriceForSku(whitelist, sku) {
  if (!whitelist || !whitelist.length) {
    return null;
  }
  var entry = whitelist.find(item => item.sku === sku);
  return entry ? entry.price : null;
}

/**
 * @param {{ sku: string, price: number }[]|null|undefined} whitelist
 * @param {string} sku
 */
export function isOnlyItemAtPriceSku(whitelist, sku) {
  return getOnlyItemAtPriceForSku(whitelist, sku) != null;
}
export function isOnlyItemAtPriceMenuActive(whitelist) {
  return Boolean(whitelist && whitelist.length);
}

/**
 * Collect all product SKUs under a menu section tree.
 * @param {object} section
 * @returns {string[]}
 */
export function getAllProductSkusFromSection(section) {
  var skus = (section.products || []).map(product => product.sku);
  if (section.sections && section.sections.length > 0) {
    section.sections.forEach(nestedSection => {
      skus = skus.concat(getAllProductSkusFromSection(nestedSection));
    });
  }
  return skus;
}

/**
 * Top-level Ristorante sections that contain at least one of the given SKUs.
 * @param {object[]} productSections
 * @param {string[]} skus
 */
export function getContainingTopLevelSections(productSections, skus) {
  if (!productSections || !productSections.length || !skus || !skus.length) {
    return [];
  }
  var skuSet = new Set(skus);
  var takeawaySections = productSections.filter(section => section.products.length || section.sections.length);
  return takeawaySections.filter(section => {
    return getAllProductSkusFromSection(section).some(sku => skuSet.has(sku));
  });
}

/**
 * Hide hero tiles and category anchors when the URL whitelist targets items
 * that all belong to the same top-level menu category.
 * @param {object[]} productSections
 * @param {{ sku: string, price: number }[]|null|undefined} whitelist
 * @param {object[]} filteredProducts
 */
export function shouldHideHeroAndAnchorsForOnlyItemAtPrice(productSections, whitelist, filteredProducts) {
  if (!whitelist || !whitelist.length) {
    return false;
  }
  var whitelistSkus = whitelist.map(entry => entry.sku);
  var visibleSkus = (filteredProducts || []).filter(product => whitelistSkus.includes(product.sku)).map(product => product.sku);
  if (!visibleSkus.length) {
    return false;
  }
  var containingSections = getContainingTopLevelSections(productSections, visibleSkus);
  if (containingSections.length !== 1) {
    return false;
  }
  var containingSectionSkus = new Set(getAllProductSkusFromSection(containingSections[0]));
  return visibleSkus.every(sku => containingSectionSkus.has(sku));
}

/**
 * Apply URL price override to a catalog product. Preserves the Magento price on `catalog_price`.
 * @param {object} product
 * @param {{ sku: string, price: number }[]|null|undefined} whitelist
 */
export function applyOnlyItemAtPriceToProduct(product, whitelist) {
  var catalogPrice = product.catalog_price != null ? product.catalog_price : product.price;
  var overridePrice = getOnlyItemAtPriceForSku(whitelist, product.sku);
  var productWithCatalogPrice = product.catalog_price != null ? product : _objectSpread(_objectSpread({}, product), {}, {
    catalog_price: catalogPrice
  });
  if (overridePrice == null) {
    if (product.only_item_at_price_override) {
      return _objectSpread(_objectSpread({}, productWithCatalogPrice), {}, {
        price: catalogPrice,
        only_item_at_price_override: false
      });
    }
    return productWithCatalogPrice;
  }
  return _objectSpread(_objectSpread({}, productWithCatalogPrice), {}, {
    price: overridePrice,
    only_item_at_price_override: true
  });
}

/** @param {object[]} products @param {{ sku: string, price: number }[]|null|undefined} whitelist */
export function applyOnlyItemAtPriceToProducts(products, whitelist) {
  if (!products || !products.length) {
    return products || [];
  }
  return products.map(product => applyOnlyItemAtPriceToProduct(product, whitelist));
}

/**
 * Collect modifier option SKUs linked to whitelisted base products so options
 * remain orderable in MYO / product detail flows.
 * @param {object[]} products
 * @param {string[]} baseSkus
 */
export function getModifierSkusForProducts(products, baseSkus) {
  var baseSkuSet = new Set(baseSkus);
  var modifierSkus = new Set();
  products.forEach(product => {
    if (!baseSkuSet.has(product.sku)) {
      return;
    }
    (product.options_flat || []).forEach(option => {
      if (option.sku) {
        modifierSkus.add(option.sku);
      }
    });
  });
  return [...modifierSkus];
}

/** @deprecated Use getModifierSkusForProducts */
export function getToppingSkusForProducts(products, baseSkus) {
  return getModifierSkusForProducts(products, baseSkus);
}

/**
 * @param {object[]} filteredProducts sticker/diet filtered products
 * @param {object[]} allProducts full catalog (with price overrides applied)
 * @param {{ sku: string, price: number }[]|null|undefined} whitelist
 */
export function applyOnlyItemAtPriceFilter(filteredProducts, allProducts, whitelist) {
  if (!whitelist || !whitelist.length) {
    var visibleSkus = filteredProducts.map(product => product.sku);
    return {
      filtered_products: filteredProducts,
      filtered_sku: visibleSkus,
      orderable_sku: visibleSkus
    };
  }
  var whitelistSkus = new Set(whitelist.map(entry => entry.sku));
  var menuProducts = filteredProducts.filter(product => whitelistSkus.has(product.sku));
  var baseSkus = menuProducts.map(product => product.sku);
  var modifierSkus = getModifierSkusForProducts(allProducts, baseSkus);
  var orderableSkus = [...new Set([...baseSkus, ...modifierSkus])];
  return {
    filtered_products: menuProducts,
    filtered_sku: baseSkus,
    orderable_sku: orderableSkus
  };
}